<template>
  <transition name="modalFade">
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 animate-modal">
      <div class="w-full m-3 max-w-3xl bg-gray-800 rounded-lg shadow-xl overflow-hidden transition-all duration-200 animate-scale">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-600">
          <div class="flex items-center gap-3">
            <img
              :src="recipe.img || 'default-item.png'"
              alt="Craft Item Image"
              class="w-12 h-12 sm:h-20 sm:w-20 object-cover rounded-md border-2"
              :class="recipe.rarityColors?.borderColor || 'border-indigo-400'"
              @error="handleImageError"
            />
            <div>
              <h1 class="text-lg font-semibold" :class="recipe.rarityColors?.textColor || 'text-indigo-200'">
                {{ recipe.name || 'ไม่ระบุ' }}
              </h1>
              <p class="text-indigo-200 text-xs sm:text-sm">ประเภท: {{ recipe.type || 'ไม่ระบุ' }} {{ recipe.subtype ? `(${recipe.subtype})` : '' }}</p>
              <div v-if="recipe.rarity" class="flex items-center gap-1 mt-1">
                <i v-for="star in iterableRarity" :key="star" class="fas fa-star text-yellow-400 text-xs sm:text-sm"></i>
              </div>
            </div>
          </div>
          <button @click="close" class="text-gray-300 hover:text-indigo-300 group">
            <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
          </button>
        </div>
        <!-- Tabs -->
        <div class="p-4">
          <div class="flex border-b border-gray-600">
            <button
              @click="activeTab = 'crafting'"
              class="px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200"
              :class="activeTab === 'crafting' ? 'text-indigo-200 border-b-2 border-indigo-400' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
              :aria-selected="activeTab === 'crafting'"
              aria-label="ข้อมูลการคราฟ"
            >
              ข้อมูลการคราฟ
            </button>
            <button
              @click="activeTab = 'info'"
              class="px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200"
              :class="activeTab === 'info' ? 'text-indigo-200 border-b-2 border-indigo-400' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
              :aria-selected="activeTab === 'info'"
              aria-label="ข้อมูลไอเทม"
            >
              ข้อมูลไอเทม
            </button>
          </div>
        </div>
        <!-- Content -->
        <div class="px-4 py-4 space-y-4 overflow-y-auto max-h-60 scrollbar-thin">
          <!-- Error Message -->
          <div v-if="modalError" class="text-red-300 text-xs sm:text-sm text-center mb-4">
            {{ modalError }}
          </div>
          <!-- Tab: Crafting Info -->
          <div v-if="activeTab === 'crafting'">
            <!-- Materials Required -->
            <section class="bg-gray-900 bg-opacity-80 rounded-md p-3 border border-gray-800">
              <h2 class="text-xs sm:text-sm font-semibold text-indigo-200 mb-2">วัตถุดิบที่ต้องใช้</h2>
              <div class="grid grid-cols-1 gap-3">
                <div v-for="material in sortedMaterials" :key="material.itemName" class="flex items-center gap-3">
                  <img
                    :src="material.img || 'default-item.png'"
                    alt="Material Image"
                    class="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded-full border border-gray-600"
                    :class="material.rarityColors?.borderColor || 'border-indigo-600'"
                    @error="handleImageError"
                  />
                  <div class="flex-1">
                    <p class="text-xs sm:text-sm font-bold" :class="material.rarityColors?.textColor || 'text-indigo-200'">
                      {{ material.name || material.itemName }}
                    </p>
                    <span class="text-xs sm:text-sm" :class="hasEnoughMaterial(material, craftQuantity) ? 'text-green-400' : 'text-red-400'">
                      จำนวน {{ getMaterialCount(material.itemName) }}/{{ material.quantity * craftQuantity }} ชิ้น
                    </span>
                  </div>
                </div>
              </div>
            </section>
            <!-- Quantity Selector -->
            <section class="bg-gray-900 bg-opacity-80 rounded-md p-3 border border-gray-800 mt-4">
              <h2 class="text-xs sm:text-sm font-semibold text-indigo-200 mb-2">จำนวนที่ต้องการคราฟ</h2>
              <div class="flex items-center gap-2">
                <label class="text-sm text-indigo-200">จำนวน:</label>
                <div class="flex items-center bg-gray-700 border border-gray-600 rounded-md overflow-hidden">
                  <button
                    @click="decreaseQuantity"
                    :disabled="craftQuantity <= 1"
                    class="px-3 py-1.5 text-indigo-200 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <i class="fas fa-minus text-xs"></i>
                  </button>
                  <span class="px-4 py-1.5 text-indigo-200 font-semibold text-sm border-x border-gray-600">
                    {{ craftQuantity }}
                  </span>
                  <button
                    @click="increaseQuantity"
                    :disabled="craftQuantity >= maxCraftQuantity"
                    class="px-3 py-1.5 text-indigo-200 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <i class="fas fa-plus text-xs"></i>
                  </button>
                </div>
              </div>
            </section>
            <!-- Required Intelligence -->
            <section v-if="recipe.requiredInt" class="bg-gray-900 bg-opacity-80 rounded-md p-3 border border-gray-800 mt-4">
              <h2 class="text-xs sm:text-sm font-semibold text-indigo-200 mb-2">ความฉลาดที่ต้องใช้</h2>
              <p class="text-xs sm:text-sm text-indigo-300">
                ความฉลาด: <strong>{{ recipe.requiredInt }}</strong>
                <span :class="hasEnoughInt ? 'text-green-400' : 'text-red-400'">
                  (คุณมี: {{ totalStats.int || 0 }})
                </span>
              </p>
            </section>
            <!-- Required Skill -->
            <section v-if="recipe.requiredSkill" class="bg-gray-900 bg-opacity-80 rounded-md p-3 border border-gray-800 mt-4">
              <h2 class="text-xs sm:text-sm font-semibold text-indigo-200 mb-2">ทักษะที่ต้องใช้</h2>
              <p class="text-xs sm:text-sm text-indigo-300">
                ทักษะ: <strong>{{ recipe.requiredSkill }}</strong>
                <span :class="hasRequiredSkill(recipe.requiredSkill) ? 'text-green-400' : 'text-red-400'">
                  (คุณ{{ hasRequiredSkill(recipe.requiredSkill) ? 'มี' : 'ไม่มี' }}ทักษะนี้)
                </span>
              </p>
            </section>
            <!-- Required Previous Spirits -->
            <section v-if="recipe.requiredPreviousSpirits && recipe.requiredPreviousSpirits.length > 0" class="bg-gray-900 bg-opacity-80 rounded-md p-3 border border-gray-800 mt-4">
              <h2 class="text-xs sm:text-sm font-semibold text-indigo-200 mb-2">วิญญาณที่ต้องเคยอัญเชิญมาก่อน (อย่างน้อยหนึ่งตัว)</h2>
              <p class="text-xs sm:text-sm text-indigo-300">
                {{ recipe.requiredPreviousSpirits.join(', ') }}
                <span :class="hasRequiredPreviousSpirits ? 'text-green-400' : 'text-red-400'">
                  (คุณ{{ hasRequiredPreviousSpirits ? 'มี' : 'ไม่มี' }})
                </span>
              </p>
            </section>
            <!-- Additional Crafting Info -->
            <section class="bg-gray-900 bg-opacity-80 rounded-md p-3 border border-gray-800 mt-4">
              <h2 class="text-xs sm:text-sm font-semibold text-indigo-200 mb-2">ข้อมูลการคราฟเพิ่มเติม</h2>
              <p class="text-xs sm:text-sm text-indigo-300">ประเภท: {{ recipe.type || 'ไม่ระบุ' }} {{ recipe.subtype ? `(${recipe.subtype})` : '' }}</p>
              <p v-if="recipe.expirationDate" class="text-sm text-indigo-300">
                หมดอายุ: {{ formatDate(recipe.expirationDate) }}
              </p>
              <p class="text-xs sm:text-sm text-indigo-300">
                <template v-if="maxCrafts">
                  คราฟได้: {{ craftInfo.count || 0 }}/{{ maxCrafts }} ครั้ง
                </template>
                <template v-else>คราฟได้ไม่จำกัดจำนวน</template>
              </p>
              <p class="text-xs sm:text-sm text-indigo-300">โอกาสโอนได้: {{ recipe.transferChance || 0 }}%</p>
              <p class="text-xs sm:text-sm text-indigo-300">โอกาสสำเร็จ: {{ recipe.successChance || 100 }}%</p>
            </section>
          </div>
          <!-- Tab: Item Info -->
          <div v-if="activeTab === 'info'">
            <!-- Basic Information Section -->
            <section>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="recipe.type !== 'ทั่วไป'" class="flex items-center gap-2">
                  <span class="text-indigo-200">เลเวลที่ใช้ได้: <strong>{{ recipe.requiredLevel || 1 }}</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">น้ำหนัก: <strong>{{ recipe.weight || 0 }}</strong> กก.</span>
                </div>
                <div class="flex items-center gap-2">
                  <i v-if="recipe.currencyType === 'gold'" class="fa-solid fa-g text-yellow-400"></i>
                  <i v-else-if="recipe.currencyType === 'diamond'" class="fa-solid fa-gem text-blue-400"></i>
                  <i v-else class="fa-solid fa-coins text-gray-100"></i>
                  <span class="text-indigo-200">ราคา: <strong>{{ (recipe.price || 0).toLocaleString() }}</strong> </span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">สวมใส่ได้: <strong>{{ recipe.wearable ? 'ใช่' : 'ไม่' }}</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">โอนได้: <strong>{{ recipe.transfer ? 'ใช่' : 'ไม่' }}</strong></span>
                </div>
              </div>
            </section>
            <!-- Description Section -->
            <section v-if="recipe.description" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">คำอธิบาย</h2>
              <p class="text-indigo-200 text-xs sm:text-sm leading-relaxed bg-gray-700 p-3 rounded-md">
                {{ recipe.description }}
              </p>
            </section>
            <!-- Statistics Section -->
            <section v-if="hasStats" class="mt-4">
              <div v-if="recipe.stats?.stamina >= 1 || recipe.stats?.str >= 1 || recipe.stats?.agi >= 1 || recipe.stats?.int >= 1 || recipe.stats?.luck >= 1">
                <h2 class="text-sm font-semibold text-indigo-200 mb-2">ค่าสถานะหลัก</h2>
                <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                  <div v-if="recipe.stats?.stamina" class="flex items-center gap-2">
                    <span class="text-indigo-200">Stamina: <strong>{{ recipe.stats.stamina }}</strong></span>
                  </div>
                  <div v-if="recipe.stats?.str" class="flex items-center gap-2">
                    <span class="text-indigo-200">Str: <strong>{{ recipe.stats.str }}</strong></span>
                  </div>
                  <div v-if="recipe.stats?.agi" class="flex items-center gap-2">
                    <span class="text-indigo-200">Agi: <strong>{{ recipe.stats.agi }}</strong></span>
                  </div>
                  <div v-if="recipe.stats?.int" class="flex items-center gap-2">
                    <span class="text-indigo-200">Int: <strong>{{ recipe.stats.int }}</strong></span>
                  </div>
                  <div v-if="recipe.stats?.luck" class="flex items-center gap-2">
                    <span class="text-indigo-200">Luck: <strong>{{ recipe.stats.luck }}</strong></span>
                  </div>
                </div>
              </div>
              <div v-if="recipe.stats?.maxHp >= 1 || recipe.stats?.atk >= 1 || recipe.stats?.evasion >= 1 || recipe.stats?.critical >= 1 || recipe.stats?.defense >= 1" class="mt-4">
                <h2 class="text-sm font-semibold text-indigo-200 mb-2">ค่าสถานะเสริม</h2>
                <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                  <div v-if="recipe.stats?.maxHp" class="flex items-center gap-2">
                    <span class="text-indigo-200">พลังชีวิต: <strong>{{ recipe.stats.maxHp }}</strong></span>
                  </div>
                  <div v-if="recipe.stats?.atk" class="flex items-center gap-2">
                    <span class="text-indigo-200">พลังโจมตี: <strong>{{ recipe.stats.atk }}</strong></span>
                  </div>
                  <div v-if="recipe.stats?.evasion" class="flex items-center gap-2">
                    <span class="text-indigo-200">หลบหลีก: <strong>{{ recipe.stats.evasion }}</strong></span>
                  </div>
                  <div v-if="recipe.stats?.critical" class="flex items-center gap-2">
                    <span class="text-indigo-200">คริติคอล: <strong>{{ recipe.stats.critical }}</strong></span>
                  </div>
                  <div v-if="recipe.stats?.defense" class="flex items-center gap-2">
                    <span class="text-indigo-200">พลังป้องกัน: <strong>{{ recipe.stats.defense }}</strong></span>
                  </div>
                </div>
              </div>
            </section>
            <!-- Food-Specific Info -->
            <section v-if="recipe.type === 'อาหาร'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลอาหาร</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">ระดับอาหาร: <strong>{{ recipe.foodLevel || 0 }}</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">ระดับความสุข: <strong>{{ recipe.happinessLevel || 0 }}</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">ระดับพลังงาน: <strong>{{ recipe.energyLevel || 0 }}</strong></span>
                </div>
              </div>
            </section>
            <!-- Healing-Specific Info -->
            <section v-if="recipe.type === 'รักษา'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลการรักษา</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">HP ที่ได้รับ: <strong>{{ recipe.healHpPercentage || 0 }}%</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">รักษาไวรัส: <strong>{{ recipe.virusHealPercentage || 0 }}%</strong></span>
                </div>
              </div>
            </section>
            <!-- Trap-Specific Info -->
            <section v-if="recipe.type === 'กัปดัก'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลกัปดัก</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="recipe.diceCount" class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนลูกเต๋า: <strong>{{ recipe.diceCount }}</strong></span>
                </div>
                <div v-if="recipe.damage" class="flex items-center gap-2">
                  <span class="text-indigo-200">สร้างความเสียหาย: <strong>{{ recipe.damage }}</strong></span>
                </div>
                <div v-if="recipe.durationHours" class="flex items-center gap-2">
                  <span class="text-indigo-200">ระยะเวลาชั่วโมง: <strong>{{ recipe.durationHours }}</strong></span>
                </div>
                <div v-if="recipe.useCount" class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนครั้งที่ใช้งานได้: <strong>{{ recipe.useCount }}</strong></span>
                </div>
              </div>
            </section>
            <!-- Bomb-Specific Info -->
            <section v-if="recipe.type === 'ระเบิด'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลระเบิด</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="recipe.damage" class="flex items-center gap-2">
                  <span class="text-indigo-200">ความเสียหายที่จะได้รับ: <strong>{{ recipe.damage }}</strong></span>
                </div>
              </div>
            </section>
            <!-- Vehicle-Specific Info -->
            <section v-if="recipe.type === 'พาหนะ'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลพาหนะ</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="recipe.fuelLevel" class="flex items-center gap-2">
                  <span class="text-indigo-200">ระดับน้ำมัน: <strong>{{ recipe.fuelLevel }}</strong></span>
                </div>
              </div>
            </section>
            <!-- Disarm Trap-Specific Info -->
            <section v-if="recipe.type === 'ปลดกัปดัก'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลปลดกัปดัก</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="recipe.disarmRoomCount" class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนห้องที่สามารถปลดกัปดักได้: <strong>{{ recipe.disarmRoomCount }}</strong></span>
                </div>
              </div>
            </section>
            <!-- Loot-Specific Info -->
            <section v-if="recipe.type === 'ปล้น'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลการปล้น</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">ระยะเวลาที่ต้องรอ: <strong>{{ recipe.waitTimeMinutes || 0 }}</strong> นาที</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนไอเทมที่ปล้นได้ขั้นต่ำ: <strong>{{ recipe.minLootItems || 0 }}</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนไอเทมที่ปล้นได้ขั้นสูงสุด: <strong>{{ recipe.maxLootItems || 0 }}</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนเหรียญที่ได้รับหลังชนะ: <strong>{{ recipe.silverReward || 0 }}</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนทองที่ได้รับหลังชนะ: <strong>{{ recipe.goldReward || 0 }}</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนเพชรที่ได้รับหลังชนะ: <strong>{{ recipe.diamondReward || 0 }}</strong></span>
                </div>
              </div>
            </section>
            <!-- Killer Robot-Specific Info -->
            <section v-if="recipe.type === 'หุ่นยนต์สังหาร'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลหุ่นยนต์สังหาร</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนครั้งที่อัปเกรดได้: <strong>{{ recipe.maxUpgradeCount || 0 }}</strong></span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">น้ำหนักที่หุ่นยนต์สามารถรับได้: <strong>{{ recipe.maxLoad || 0 }}</strong> กก.</span>
                </div>
              </div>
              <div v-if="recipe.explorableMaps && recipe.explorableMaps.length > 0" class="mt-3">
                <h3 class="text-xs font-semibold text-indigo-200 mb-2">แผนที่ที่สามารถสำรวจได้</h3>
                <div class="flex flex-wrap gap-2">
                  <span v-for="(map, index) in recipe.explorableMaps" :key="index" class="px-2 py-1 text-xs text-indigo-200 bg-gray-600 rounded-md">
                    {{ map }}
                  </span>
                </div>
              </div>
              <div v-if="recipe.upgradeMaterials && recipe.upgradeMaterials.length > 0" class="mt-3">
                <h3 class="text-xs font-semibold text-indigo-200 mb-2">ไอเทมที่ใช้ในการอัปเกรด</h3>
                <div class="space-y-2">
                  <div v-for="(material, index) in recipe.upgradeMaterials" :key="index" class="flex items-center gap-2 text-indigo-200">
                    <span>{{ material.itemName }}: <strong>{{ material.quantity }}</strong></span>
                  </div>
                </div>
              </div>
            </section>
            <!-- Cybernetic Organ-Specific Info -->
            <section v-if="recipe.type === 'อวัยวะจักรกล'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลอวัยวะจักรกล</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="recipe.upgradeableStats && recipe.upgradeableStats.length > 0" class="col-span-2">
                  <h3 class="text-xs font-semibold text-indigo-200 mb-2">ค่าสถานะที่สามารถอัปเกรดได้</h3>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="stat in recipe.upgradeableStats" :key="stat" class="px-2 py-1 text-xs text-indigo-200 bg-gray-600 rounded-md">
                      {{ stat.charAt(0).toUpperCase() + stat.slice(1) }}
                    </span>
                  </div>
                </div>
              </div>
            </section>
            <!-- Card-Specific Info -->
            <section v-if="recipe.type === 'การ์ด'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลการ์ด</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div class="flex items-center gap-2">
                  <span class="text-indigo-200">ชื่ออวัยวะจักรกลที่สามารถติดตั้งการ์ดได้: <strong>{{ recipe.compatibleEquipment || 'ไม่ระบุ' }}</strong></span>
                </div>
              </div>
            </section>
            <!-- Spirit-Specific Info -->
            <section v-if="recipe.type === 'วิญญาณ'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลวิญญาณ</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="recipe.subtype === 'วิญญาณเฝ้าห้อง' && recipe.guardDurationHours" class="flex items-center gap-2">
                  <span class="text-indigo-200">ระยะเวลาที่เฝ้า: <strong>{{ recipe.guardDurationHours }}</strong> ชั่วโมง</span>
                </div>
                <div v-if="recipe.subtype === 'วิญญาณเฝ้าห้อง' && recipe.fromMap" class="flex items-center gap-2">
                  <span class="text-indigo-200">วิญญาณจากแผนที่: <strong>{{ recipe.fromMap }}</strong></span>
                </div>
                <div v-if="recipe.subtype === 'วิญญาณเฝ้าห้อง' && recipe.minSpirits" class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนวิญญาณขั้นต่ำ: <strong>{{ recipe.minSpirits }}</strong></span>
                </div>
                <div v-if="recipe.subtype === 'วิญญาณเฝ้าห้อง' && recipe.maxSpirits" class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนวิญญาณสูงสุด: <strong>{{ recipe.maxSpirits }}</strong></span>
                </div>
                <div v-if="['วิญญาณเฝ้าห้อง', 'วิญญาณผู้พิทักษ์', 'วิญญาณดูดทรัพย์'].includes(recipe.subtype) && recipe.cooldownHours" class="flex items-center gap-2">
                  <span class="text-indigo-200">ระยะเวลาคูดาวน์การใช้ครั้งถัดไป: <strong>{{ recipe.cooldownHours }}</strong> ชั่วโมง</span>
                </div>
                <div v-if="recipe.subtype === 'วิญญาณค้นหาผู้เล่น' && recipe.eliminatedRooms" class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนห้องที่ถูกคัดออก: <strong>{{ recipe.eliminatedRooms }}</strong></span>
                </div>
                <div v-if="recipe.subtype === 'วิญญาณผู้พิทักษ์' && recipe.protectCount" class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนเกราะที่สามารถป้องกันได้: <strong>{{ recipe.protectCount }}</strong></span>
                </div>
                <div v-if="recipe.subtype === 'วิญญาณผู้พิทักษ์' && recipe.protectDurationHours" class="flex items-center gap-2">
                  <span class="text-indigo-200">ระยะเวลาคุ้มกัน: <strong>{{ recipe.protectDurationHours }}</strong> ชั่วโมง</span>
                </div>
                <div v-if="recipe.subtype === 'วิญญาณดูดทรัพย์' && recipe.minDiamondLoot" class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนปล้นเพชรขั้นต่ำ: <strong>{{ recipe.minDiamondLoot }}</strong></span>
                </div>
                <div v-if="recipe.subtype === 'วิญญาณดูดทรัพย์' && recipe.maxDiamondLoot" class="flex items-center gap-2">
                  <span class="text-indigo-200">จำนวนปล้นเพชรขั้นสูงสุด: <strong>{{ recipe.maxDiamondLoot }}</strong></span>
                </div>
              </div>
              <!-- Required Previous Spirits Section -->
              <div v-if="recipe.requiredPreviousSpirits && recipe.requiredPreviousSpirits.length > 0" class="mt-3">
                <h3 class="text-xs font-semibold text-indigo-200 mb-2">วิญญาณที่ต้องเคยอัญเชิญมาก่อน</h3>
                <div class="flex flex-wrap gap-2">
                  <span v-for="spirit in recipe.requiredPreviousSpirits" :key="spirit" class="px-2 py-1 text-xs text-indigo-200 bg-gray-600 rounded-md">
                    {{ spirit }}
                  </span>
                </div>
              </div>
            </section>
            <!-- Book-Specific Info -->
            <section v-if="recipe.type === 'หนังสือ'" class="mt-4">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลหนังสือ</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-if="recipe.order" class="flex items-center gap-2">
                  <span class="text-indigo-200">ลำดับ: <strong>{{ recipe.order }}</strong></span>
                </div>
              </div>
            </section>
          </div>
        </div>
        <!-- Footer -->
        <div class="p-4 border-t border-gray-600 flex justify-end gap-4">
          <button
            v-if="activeTab === 'crafting'"
            @click="craft"
            :class="[
              'flex items-center bg-indigo-600 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-indigo-700 text-sm group',
              {
                'opacity-60 cursor-not-allowed':
                  isCrafting ||
                  isExpired() ||
                  !hasEnoughMaterials(craftQuantity) ||
                  !hasEnoughInt ||
                  (recipe.requiredSkill && !hasRequiredSkill(recipe.requiredSkill)) ||
                  !hasRequiredPreviousSpirits ||
                  craftQuantity < 1 ||
                  craftQuantity > maxCraftQuantity,
              },
            ]"
            :disabled="
              isCrafting ||
              isExpired() ||
              !hasEnoughMaterials(craftQuantity) ||
              !hasEnoughInt ||
              (recipe.requiredSkill && !hasRequiredSkill(recipe.requiredSkill)) ||
              !hasRequiredPreviousSpirits ||
              craftQuantity < 1 ||
              craftQuantity > maxCraftQuantity
            "
          >
            <span v-if="!isCrafting">
              <i class="fas fa-hammer mr-1 text-sm group-hover:scale-110"></i> คราฟ
            </span>
            <span v-else>
              <i class="fas fa-spinner animate-spin text-sm"></i>
            </span>
          </button>
          <button
            @click="close"
            class="flex items-center bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-700 text-sm group"
          >
            <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i> ปิด
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>
<script>
import { ref, computed, watch } from 'vue';
import dayjs from 'dayjs';
export default {
  name: 'CraftModal',
  props: {
    recipe: {
      type: Object,
      required: true,
    },
    totalStats: {
      type: Object,
      required: true,
    },
    inventoryItems: {
      type: Array,
      required: true,
    },
    characterPrivateData: {
      type: Object,
      required: true,
    },
    isCrafting: {
      type: Boolean,
      required: true,
    },
    craftInfo: {
      type: Object,
      required: true,
    },
    maxCrafts: {
      type: Number,
      default: null,
    },
  },
  emits: ['craft', 'close'],
  setup(props, { emit }) {
    const activeTab = ref('crafting');
    const modalError = ref('');
    const craftQuantity = ref(1);
    // Computed property to sort materials by quantity (ascending)
    const sortedMaterials = computed(() => {
      return [...props.recipe.materials].sort((a, b) => a.quantity - b.quantity);
    });
    // Compute maximum craft quantity
    const maxCraftQuantity = computed(() => {
      if (!props.recipe?.materials || !props.inventoryItems) return 1;
      // Calculate max crafts based on available materials
      let maxCraftsByMaterials = Infinity;
      props.recipe.materials.forEach((material) => {
        const available = props.inventoryItems.reduce((sum, item) => {
          if (item.name === material.itemName) {
            return sum + (item.quantity || 1);
          }
          return sum;
        }, 0);
        const maxForMaterial = Math.floor(available / material.quantity);
        maxCraftsByMaterials = Math.min(maxCraftsByMaterials, maxForMaterial);
      });
      // Factor in maxCrafts limit if provided
      const remainingCrafts = props.maxCrafts !== null
        ? props.maxCrafts - (props.craftInfo?.count || 0)
        : Infinity;
      // Return the minimum of material-based limit, remaining crafts, and a reasonable cap (e.g., 10)
      return Math.min(maxCraftsByMaterials, remainingCrafts, 10);
    });
    // Format date
    const formatDate = (dateString) => {
      if (!dateString) return 'ไม่มีกำหนด';
      const date = dayjs(dateString);
      if (!date.isValid()) return 'วันที่ไม่ถูกต้อง';
      return date.format('DD/MM/YYYY');
    };
    // Check if recipe is expired
    const isExpired = () => {
      if (!props.recipe?.expirationDate) return false;
      const expiration = dayjs(props.recipe.expirationDate);
      const now = dayjs();
      return now.isAfter(expiration);
    };
    // Check if user has required skill
    const hasRequiredSkill = (requiredSkill) => {
      if (!requiredSkill || !props.characterPrivateData?.learnedSkills) {
        return true;
      }
      return props.characterPrivateData.learnedSkills.includes(requiredSkill);
    };
    // Check if user has required previous spirits
    const hasRequiredPreviousSpirits = computed(() => {
      if (!props.recipe.requiredPreviousSpirits || props.recipe.requiredPreviousSpirits.length === 0) return true;
      const history = props.characterPrivateData?.summonedSpiritsHistory || [];
      return props.recipe.requiredPreviousSpirits.some(req => history.includes(req));
    });
    // Compute material counts
    const materialCounts = computed(() => {
      const counts = {};
      props.inventoryItems.forEach((item) => {
        counts[item.name] = (counts[item.name] || 0) + (item.quantity || 1);
      });
      return counts;
    });
    // Get material count
    const getMaterialCount = (itemName) => {
      return materialCounts.value[itemName] || 0;
    };
    // Check if enough materials for the specified quantity
    const hasEnoughMaterials = (quantity) => {
      if (!props.recipe?.materials) return false;
      return props.recipe.materials.every((material) => {
        const available = materialCounts.value[material.itemName] || 0;
        return available >= material.quantity * quantity;
      });
    };
    // Check if enough material for one item with specified quantity
    const hasEnoughMaterial = (material, quantity) => {
      const available = materialCounts.value[material.itemName] || 0;
      return available >= material.quantity * quantity;
    };
    // Check if enough intelligence
    const hasEnoughInt = computed(() => {
      return props.totalStats.int >= (props.recipe.requiredInt || 0);
    });
    // Check if item has stats
    const hasStats = computed(() => {
      const s = props.recipe.stats || {};
      return (
        s.stamina ||
        s.str ||
        s.agi ||
        s.int ||
        s.luck ||
        s.maxHp ||
        s.atk ||
        s.defense ||
        s.evasion ||
        s.critical
      );
    });
    // Handle image errors
    const handleImageError = (event) => {
      event.target.src = 'default-item.png';
    };
    // Computed iterable rarity
    const iterableRarity = computed(() => {
      return Array.from({ length: parseInt(props.recipe.rarity) || 0 }, (_, i) => i + 1);
    });
    // Increase quantity
    const increaseQuantity = () => {
      if (craftQuantity.value < maxCraftQuantity.value) {
        craftQuantity.value++;
      }
    };
    // Decrease quantity
    const decreaseQuantity = () => {
      if (craftQuantity.value > 1) {
        craftQuantity.value--;
      }
    };
    // Reset quantity when recipe changes
    watch(() => props.recipe, () => {
      craftQuantity.value = 1;
    });
    // Emit craft event with quantity
    const craft = () => {
      if (craftQuantity.value < 1 || craftQuantity.value > maxCraftQuantity.value) {
        modalError.value = 'จำนวนคราฟไม่ถูกต้อง';
        return;
      }
      emit('craft', craftQuantity.value);
    };
    // Emit close event
    const close = () => {
      modalError.value = '';
      craftQuantity.value = 1;
      emit('close');
    };
    return {
      activeTab,
      modalError,
      sortedMaterials,
      formatDate,
      isExpired,
      hasRequiredSkill,
      getMaterialCount,
      hasEnoughMaterials,
      hasEnoughMaterial,
      hasEnoughInt,
      hasStats,
      handleImageError,
      iterableRarity,
      craft,
      close,
      craftQuantity,
      maxCraftQuantity,
      increaseQuantity,
      decreaseQuantity,
      hasRequiredPreviousSpirits,
    };
  },
};
</script>
<style scoped>
.animate-modal {
  animation: fadeIn 0.3s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-scale {
  animation: scaleIn 0.2s ease-in-out;
}
@keyframes scaleIn {
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
button:not(:disabled):active {
  transform: scale(0.98);
}
</style>